import React, { useCallback } from 'react';
import { ChevronLeft, Download, AlertTriangle, Info } from 'lucide-react';

/*
 * Funnel Cohort Report display (P1b). Pure presentation over the aggregate
 * payload from reports:funnel:run — counts/%/$ only, no PHI. Renders:
 *   - cohort size (= New Lead = 100%)
 *   - the monotonic progression ladder (8 buckets) with % of cohort + stage-to-
 *     stage retention
 *   - terminal Lost / Nurture counts (side-states, not on the ladder)
 *   - cohort booked (won) revenue = sum of won-opp value
 *   - a tracking-start note when the range predates P1a recording (approximation)
 *   - an "N leads unmapped" footnote (their current stage has no bucket row)
 * CSV export is built client-side from the already-returned aggregate (no new
 * data access → no re-audit needed).
 */

const card = { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #DEE2E6', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#495057' };
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export default function FunnelReport({ data, onBack }) {
  const exportCsv = useCallback(() => {
    if (!data) return;
    const q = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const lines = [];
    lines.push(['Bucket', 'Count', '% of cohort', '% of prior stage'].map(q).join(','));
    for (const r of data.ladder || []) {
      lines.push([r.bucket, r.count, `${r.pctOfCohort}%`, r.pctOfPrior == null ? '' : `${r.pctOfPrior}%`].map(q).join(','));
    }
    lines.push('');
    lines.push([q('Cohort size'), data.cohortSize].join(','));
    lines.push([q('Terminal — Lost'), data.terminal?.lost ?? 0].join(','));
    lines.push([q('Terminal — Nurture'), data.terminal?.nurture ?? 0].join(','));
    lines.push([q('Booked (won) revenue'), money(data.revenueBookedWon)].join(','));
    if (data.unmapped?.count) lines.push([q('Unmapped (current stage)'), data.unmapped.count].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'funnel_cohort.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }, [data]);

  if (!data) return null;
  const { cohortSize, ladder = [], terminal = {}, revenueBookedWon, trackingStart, approximated, unmapped } = data;
  const maxCount = ladder.length ? ladder[0].count : 0; // New Lead = cohort ceiling for the bars

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <button style={btn} onClick={onBack}><ChevronLeft size={14} /> Back</button>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#212529' }}>Funnel (cohort)</div>
        <span style={{ fontSize: 12, color: '#ADB5BD' }}>
          cohort of {cohortSize.toLocaleString('en-US')} · {fmtDate(data.range?.from)} → {data.range?.to ? fmtDate(data.range.to) : 'now'}
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <button style={btn} onClick={exportCsv}><Download size={13} /> Export CSV</button>
        </div>
      </div>

      {/* Tracking-start approximation note */}
      {approximated && (
        <div style={{ ...card, background: '#FFF8E6', border: '1px solid #FDE68A', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Info size={18} color="#B45309" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: '#7C5E10', lineHeight: 1.5 }}>
            <strong>Approximation for part of this range.</strong> True cohort progression is recorded only from{' '}
            <strong>{trackingStart ? fmtDate(trackingStart) : '(no live transitions recorded yet)'}</strong> onward
            (Stage History deploy). Leads created before then show their <em>current</em> stage as their furthest
            position, which understates mid-funnel drop-off that happened pre-recording.
          </div>
        </div>
      )}

      {/* The ladder */}
      <div style={{ ...card, marginBottom: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#6C757D', fontSize: 12 }}>
              <th style={{ padding: '6px 8px' }}>Stage</th>
              <th style={{ padding: '6px 8px', width: '38%' }}>Reached</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Count</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>% of cohort</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>vs prior</th>
            </tr>
          </thead>
          <tbody>
            {ladder.map((r) => (
              <tr key={r.sortOrder} style={{ borderTop: '1px solid #F1F3F5' }}>
                <td style={{ padding: '8px', fontWeight: 600, color: '#212529' }}>{r.bucket}</td>
                <td style={{ padding: '8px' }}>
                  <div style={{ background: '#E7F4F6', borderRadius: 4, height: 16, width: `${maxCount ? Math.round((r.count / maxCount) * 100) : 0}%`, minWidth: r.count ? 2 : 0 }} />
                </td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{r.count.toLocaleString('en-US')}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{r.pctOfCohort}%</td>
                <td style={{ padding: '8px', textAlign: 'right', color: '#6C757D' }}>{r.pctOfPrior == null ? '—' : `${r.pctOfPrior}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Terminal side-states + revenue */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ ...card, flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: 12, color: '#6C757D' }}>Lost (terminal)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#DC2626' }}>{(terminal.lost || 0).toLocaleString('en-US')}</div>
        </div>
        <div style={{ ...card, flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: 12, color: '#6C757D' }}>Nurture (terminal)</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#D97706' }}>{(terminal.nurture || 0).toLocaleString('en-US')}</div>
        </div>
        <div style={{ ...card, flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 12, color: '#6C757D' }}>Booked (won) revenue</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#2E8B98' }}>{money(revenueBookedWon)}</div>
        </div>
      </div>

      {/* Unmapped footnote */}
      {unmapped?.count > 0 && (
        <div style={{ ...card, background: '#FDECEA', border: '1px solid #F5C6C0', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertTriangle size={18} color="#C0392B" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: '#7B241C', lineHeight: 1.5 }}>
            <strong>{unmapped.count.toLocaleString('en-US')} lead{unmapped.count === 1 ? '' : 's'} unmapped.</strong> Their
            current stage has no funnel-bucket mapping, so they're counted in the cohort but not classified into a
            terminal state:{' '}
            {unmapped.stages.map((s, i) => (
              <span key={s.stage}>{i > 0 ? ', ' : ''}<code>{s.stage}</code> ({s.count})</span>
            ))}
            . Add these to the bucket map to include them.
          </div>
        </div>
      )}
    </div>
  );
}
